/**
 * Dueli Internationalization (i18n) - Modular Design
 * نظام الترجمة والتعريب - التصميم المعياري
 */

// Import language translations
import { ar } from './ar';
import { en } from './en';
import { LANGUAGES, getLanguageMeta } from './languages';
export { LANGUAGES, getLanguageMeta, type LanguageMeta } from './languages';

// Languages with available translations (can be extended)
export const TRANSLATED_LANGUAGES = ['ar', 'en'] as const;
export type TranslatedLanguage = typeof TRANSLATED_LANGUAGES[number];

// Language can be ANY language code (all languages supported)
// If translation not available, English is used as fallback
export type Language = string;
export const DEFAULT_LANGUAGE: TranslatedLanguage = 'en'; // English as global fallback

// Combined translations object
// T3.1: typed as Record so new languages can be registered at runtime
export const translations: Record<string, any> = { ar, en };

/**
 * T3.1: Register a translation pack at runtime (used when adding languages).
 * registerLanguage('fr', fr) — then getUILanguage/t/isRTL work immediately.
 */
export function registerLanguage(code: string, pack: Record<string, any>): void {
    translations[code] = pack;
}

/**
 * T3.1: Languages available in the switcher =
 * enabled in the registry AND having a loaded translation pack.
 */
export function getAvailableLanguages() {
    return LANGUAGES.filter(l => l.enabled && translations[l.code]);
}

// Re-export countries from countries.ts
export { countries, getCountriesList, getCountry, getCountriesByLanguage, getLocale, DEFAULT_COUNTRY, type Country } from '../countries';

// Get language code from country code
import { getCountry as getCountryFn } from '../countries';
export function getLanguageFromCountry(countryCode: string): Language {
    const country = getCountryFn(countryCode);
    if (!country) return 'en'; // Default to English (global fallback)

    // Return the country's primary language directly
    return country.primaryLang || 'en';
}

/**
 * Get the UI language for a given language code
 * Falls back to English (global fallback) if no translation available
 */
export function getUILanguage(lang: Language): TranslatedLanguage {
    if (TRANSLATED_LANGUAGES.includes(lang as TranslatedLanguage)) {
        return lang as TranslatedLanguage;
    }
    return DEFAULT_LANGUAGE; // English fallback
}

export function t(key: string, lang: Language): string {
    // 7.A compat: the task contract names the keys `live.*`, but `live` must
    // stay a plain string (all existing `tr.live` usages). Canonical storage
    // is `live_signaling.*`; accept the `live.*` alias for these keys.
    const LIVE_ALIAS: Record<string, string> = {
        'live.connecting': 'live_signaling.connecting',
        'live.connected': 'live_signaling.connected',
        'live.connection_failed': 'live_signaling.connection_failed',
        'live.permission_denied': 'live_signaling.permission_denied',
        'live.network_restricted': 'live_signaling.network_restricted',
        'live.turn_unavailable': 'live_signaling.turn_unavailable',
    };
    const resolvedKey = LIVE_ALIAS[key] ?? key;
    const keys = resolvedKey.split('.');
    const uiLang = getUILanguage(lang);
    let value: any = translations[uiLang];

    for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
            value = value[k];
        } else {
            // Fallback to English (global default)
            value = translations[DEFAULT_LANGUAGE];
            for (const k2 of keys) {
                if (value && typeof value === 'object' && k2 in value) {
                    value = value[k2];
                } else {
                    return key;
                }
            }
            break;
        }
    }

    return typeof value === 'string' ? value : key;
}

// RTL detection — T3.1: derived from the central registry (dir field),
// with a static fallback list for languages not yet in the registry.
const RTL_FALLBACK: string[] = ['fa', 'he', 'ur', 'ps', 'sd', 'ug', 'yi'];

export function isRTL(lang: Language): boolean {
    const meta = getLanguageMeta(lang);
    if (meta) return meta.dir === 'rtl';
    return RTL_FALLBACK.includes(lang);
}

export function getDir(lang: Language): 'rtl' | 'ltr' {
    return isRTL(lang) ? 'rtl' : 'ltr';
}

/**
 * Post-R1 acceptance (E) — canonical content-language normalization.
 * Only 'ar' and 'en' have backing data (e.g. `categories.name_ar/name_en`).
 * Any other value (fr, ca, xx, malformed, …) MUST fall back to the
 * application default instead of ever reaching SQL as a column identifier.
 * This is the single boundary every dynamic column selection must use.
 */
export function normalizeContentLanguage(lang: unknown): TranslatedLanguage {
    if (typeof lang === 'string') {
        const code = lang.trim().toLowerCase().split(/[^a-z]/)[0];
        if (TRANSLATED_LANGUAGES.includes(code as TranslatedLanguage)) {
            return code as TranslatedLanguage;
        }
    }
    return DEFAULT_LANGUAGE;
}

/**
 * Get localized name for category/item based on language
 * Priority: name_key (i18n) → slug as key → name_${lang} → name_en → any available
 */
export function getLocalizedName(item: Record<string, any>, lang: Language): string {
    const uiLang = getUILanguage(lang);

    // 1. Try name_key first (i18n translation lookup)
    if (item.name_key) {
        const translated = t(item.name_key, uiLang);
        // If translation found (not returning the key itself)
        if (translated !== item.name_key) return translated;
    }

    // 2. Try slug as translation key (convert 'current-affairs' to 'current_affairs')
    if (item.slug) {
        const slugKey = item.slug.replace(/-/g, '_');
        const translated = t(slugKey, uiLang);
        // If translation found (not returning the key itself)
        if (translated !== slugKey) return translated;
    }

    // 3. Try requested language (e.g., name_ar, name_fr, name_de)
    const langKey = `name_${uiLang}`;
    if (item[langKey]) return item[langKey];

    // 4. Fallback to English
    if (item.name_en) return item.name_en;

    // 5. Fallback to any available language name
    const nameKeys = Object.keys(item).filter(k => k.startsWith('name_'));
    for (const key of nameKeys) {
        if (item[key]) return item[key];
    }

    return '';
}

/**
 * Get localized category name
 * Priority: category_name_${lang}/name_${lang} (explicit DB fields) →
 * name_key (i18n) → slug as namespaced key (categories.<slug>/bare slug) →
 * English → any available.
 *
 * NOTE: explicit DB fields win over slug-key lookup on purpose. A bare slug
 * key (e.g. `physics`) may resolve to a DIFFERENT i18n pack (the legacy flat
 * block) than the caller's language, which previously returned English
 * "Physics" in an Arabic context despite name_ar being present. Slug lookup
 * is namespaced (categories.<slug> first, bare slug only as back-compat) so
 * flat-pack mismatches can never shadow the requested language again.
 */
export function getCategoryName(category: Record<string, any>, lang: Language): string {
    const uiLang = getUILanguage(lang);
    const requestedLang = TRANSLATED_LANGUAGES.includes(lang as TranslatedLanguage)
        ? (lang as TranslatedLanguage)
        : uiLang;

    // 1. Explicit DB fields in the REQUESTED language first (never English-first).
    const catReqKey = `category_name_${requestedLang}`;
    if (category[catReqKey]) return category[catReqKey];
    const nameReqKey = `name_${requestedLang}`;
    if (category[nameReqKey]) return category[nameReqKey];

    // 2. Try name_key first (i18n translation lookup)
    if (category.name_key) {
        const translated = t(category.name_key, uiLang);
        // If translation found (not returning the key itself)
        if (translated !== category.name_key) return translated;
    }

    // 3. Try slug as a NAMESPACED translation key (convert 'current-affairs' to 'current_affairs').
    // Check both 'slug' and 'category_slug' (API uses category_slug).
    // 'categories.<slug>' is authoritative; the bare slug is back-compat only
    // (it can hit legacy flat packs in the wrong language, so it comes last).
    const slug = category.slug || category.category_slug;
    if (slug) {
        const slugKey = slug.replace(/-/g, '_');
        const namespaced = t(`categories.${slugKey}`, uiLang);
        if (namespaced !== `categories.${slugKey}`) return namespaced;
        const translated = t(slugKey, uiLang);
        if (translated !== slugKey) return translated;
    }

    // 4. Explicit DB fields in the resolved UI language (uiLang may differ
    // from requestedLang only for untranslated languages → English anyway).
    const catLangKey = `category_name_${uiLang}`;
    if (category[catLangKey]) return category[catLangKey];
    const nameLangKey = `name_${uiLang}`;
    if (category[nameLangKey]) return category[nameLangKey];

    // 5. Fallback to English
    if (category.category_name_en) return category.category_name_en;
    if (category.name_en) return category.name_en;

    // 6. Fallback to any available
    const catKeys = Object.keys(category).filter(k => k.startsWith('category_name_') || k.startsWith('name_'));
    for (const key of catKeys) {
        if (category[key]) return category[key];
    }

    return '';
}

